/**
 * @file The pricebook registry: `pricebookEditions/{editionId}` with its
 * `pages`, `rules` and `parts` subcollections, the re-check and comparison
 * shapes, and the pricebook tasks sent to `/runlanguagemodel`, including the
 * start task an uploaded pricebook sends. Written only by
 * the server (the pricebook intake and the tasks, through the admin SDK); read
 * by S1's Pricebooks tab, S4, S5, the catalog grammar check and the re-check.
 * Invariants: `editionId` is `sourceSha256`, which is also the pricebook
 * job's id, so the same bytes open the same edition and its audit events live
 * at `pdfIntakeJobEventsPath(editionId)`; an edition is never edited once
 * published; `status` is derived by `pricebookEditionStatus` and written with
 * the fields it reads on every server write; every price is cited by words on
 * the edition's own evidence pages, except a value a person corrected or
 * added in S4, which carries `edited` and keeps the words it corrected.
 */
import type { DocId, TimeType } from './System.types';
import type { CheckResult, EvidenceBox, EvidencePage } from './System.Agent.PdfEvidence.types';

export const PRICEBOOK_EDITIONS_COLLECTION = 'pricebookEditions';

export function pricebookEditionPath(editionId: string): string {
  return `${PRICEBOOK_EDITIONS_COLLECTION}/${editionId}`;
}

export function pricebookEditionPagesPath(editionId: string): string {
  return `${PRICEBOOK_EDITIONS_COLLECTION}/${editionId}/pages`;
}

export function pricebookEditionRulesPath(editionId: string): string {
  return `${PRICEBOOK_EDITIONS_COLLECTION}/${editionId}/rules`;
}

export function pricebookEditionPartsPath(editionId: string): string {
  return `${PRICEBOOK_EDITIONS_COLLECTION}/${editionId}/parts`;
}

/** How a price page is printed: Part-number builder, Flat table, or Scanned · OCR. */
export type PricebookPageShape = 'builder' | 'flat' | 'scanned';

/** The two stored acts. Draft and Awaiting sign-off are derived, never stored here. */
export type PricebookPublishState = 'published' | 'superseded';

/**
 * Edition status, derived by `pricebookEditionStatus`: the stored publish
 * state when there is one; else `awaitingSignoff` exactly when page sorting
 * has shaped the pages and no page fails; else `draft`.
 */
export type PricebookEditionStatus = 'draft' | 'awaitingSignoff' | PricebookPublishState;

/** Who did a stored act and when. */
export type PricebookAct = {
  /** Firebase uid. */
  by: string;
  at: TimeType;
};

/**
 * Progress of the re-check of open schedules after a publish, written by the
 * re-check as it goes. Running while `done < total` and `running` is set
 * (`pricebookRecheckRunning`); the server sets `running` with the publish and
 * every progress write, and clears it when the re-check finishes or stops on
 * a failure, so a stopped cursor no longer holds Approve and can be resumed.
 * The dry run's counts, as the re-check reaches each schedule: how its check
 * moved (`ScheduleCatalogShift`) and whether it is left `flagged`. A schedule
 * the re-check did not check (`recheckScheduleCatalog`'s `skipped`: approved,
 * being approved or re-read meanwhile, or its staging could not be read)
 * counts as done and `skipped`, and in none of the dry run's counts.
 */
export type PricebookRecheckProgress = PricebookRecheckDryRun & {
  total: number;
  done: number;
  /** Done but not re-checked. */
  skipped: number;
  /** Server-written: true while a request is re-checking; false once it finished or stopped. */
  running: boolean;
  /** Server time of the last progress write. */
  at: TimeType;
};

/**
 * One document at `pricebookEditions/{editionId}`. Created by the pricebook
 * intake once the cover gives its manufacturer, price list and name.
 * `failingPages` holds every price page that does not pass: one not read yet,
 * one the pipeline could not read, or one with a failed check
 * (`pricebookPageFails`); it is written in the same transaction as each page.
 */
export type PricebookEdition = {
  /** Equal to `sourceSha256`. */
  docId: DocId;
  /** The manufacturer entity record id, the same id space as a schedule job's `manufacturerIds`. */
  manufacturerId: string;
  /**
   * Which price list of the manufacturer this edition replaces, such as
   * `list` or `contract`. One edition is published per manufacturer and key.
   */
  priceListKey: string;
  /** As the book prints it: "A-N-D Vol. 26", "Arcluce 2026". */
  name: string;
  /** ISO date `YYYY-MM-DD` as printed; null when the book prints none. */
  effectiveDate: string | null;
  /** Lowercase hex SHA-256 of the source file. Blocks duplicate uploads. */
  sourceSha256: string;
  /** The file name as received. */
  filename: string;
  /** Price pages by shape; null until page sorting has shaped every page. */
  pageCountsByShape: Record<PricebookPageShape, number> | null;
  /** 1-based, ascending. */
  failingPages: number[];
  /** Derived by `pricebookEditionStatus`; never written on its own. */
  status: PricebookEditionStatus;
  /**
   * The published edition for the same manufacturer and price list when this
   * one was created, shown as Replaces; the publish rewrites it to the edition
   * it actually supersedes. Null for a first edition.
   */
  previousEditionId: string | null;
  /** Absent while draft. Written only by `publishPricebookEdition`. */
  publishState?: PricebookPublishState;
  /** Absent until published. */
  published?: PricebookAct;
  /** Absent until superseded; `editionId` is the newer edition that superseded this one. */
  superseded?: PricebookAct & { editionId: string };
  /** Set by Request sign-off, which moves the edition's Waiting on to Purchasing. Null until requested. */
  signoffRequest: PricebookAct | null;
  /** Null until published. */
  recheck: PricebookRecheckProgress | null;
  /**
   * S3's Parts mappings by `FlatColumnGroup.headerKey`, so one mapping applies
   * to every column group and page printing the same header, and to a page
   * read again. Empty until a header is mapped; kept when a draft is read again.
   */
  columnMappings: Record<string, PricebookColumnMapping>;
  createdAt: TimeType;
};

/** One header cell of a flat table, rebuilt from its cited words. */
export type FlatHeaderCell = {
  /** Joined from `citedWordIds`, never the model's text. */
  header: string;
  citedWordIds: string[];
  box: EvidenceBox;
};

/** A series heading printed over rows of a flat table, rebuilt from its cited words. */
export type FlatSeriesHeading = { series: string; citedWordIds: string[]; box: EvidenceBox };

/**
 * One column group of a flat page: a run of header cells that repeats side by
 * side. `headerKey` is its headers after `normaliseForMatch`, joined by ` | `,
 * the same for every repetition on the page and on every page printing it.
 */
export type FlatColumnGroup = {
  /** 0-based, left to right. */
  index: number;
  headerKey: string;
  columns: FlatHeaderCell[];
};

/** What one column of a group lands on in Parts. `skip` leaves the column out. */
export type FlatColumnTarget =
  | { kind: 'partNumber' }
  | { kind: 'price' }
  | { kind: 'code'; fieldId: string }
  | { kind: 'skip' };

/** The S3 mapping of one header: a target per column by position, and the confirmed currency. */
export type FlatHeaderMapping = {
  columns: FlatColumnTarget[];
  /** ISO 4217, confirmed in S3 from the header or the cover. */
  currency: string;
};

/** A header mapping as S3 stores it on the edition: who confirmed it, and when. */
export type PricebookColumnMapping = FlatHeaderMapping & { confirmed: PricebookAct };

/** A currency printed on the page or the cover, proposed for S3 to confirm. */
export type PrintedCurrency = PricebookCitation & { currency: string };

/**
 * How a flat page is laid out, stored on its page so the page is derived
 * again from its own words with no model call: when S3 maps its header, and
 * when a person fixes a part. A spreadsheet sheet has one group whose header
 * cells cite no words, and no headings.
 */
export type PricebookFlatLayout = {
  columnGroups: (FlatColumnGroup & {
    /** The rows the group must give, the `rowCount` baseline; null while its header has no mapping. */
    rows: number | null;
  })[];
  seriesHeadings: FlatSeriesHeading[];
  /** Words the layout read named as neither header nor row (title, footnotes). */
  otherWordIds: string[];
  /** From the header first, then the cover; null when neither prints one. */
  proposedCurrency: PrintedCurrency | null;
};

/**
 * One document at `pricebookEditions/{editionId}/pages/{page}`, id `String(page)`,
 * for each price page. `checks` holds the page's self-checks (`sampleRebuild`
 * and `ruleRebuild` for builders, `rowCount` for flat tables, `ocrConfidence` for scanned
 * pages). `method` says how the page was read, equal to `shape` except on a
 * scanned page, which is read by either after OCR. An edition read from a
 * spreadsheet (the EnVision Excel list) has one flat page per sheet, 1-based
 * in workbook order, with `textSource` `spreadsheet` and no evidence page.
 */
export type PricebookPage = {
  docId: DocId;
  /** 1-based. */
  page: number;
  shape: PricebookPageShape;
  method: 'builder' | 'flat';
  textSource: EvidencePage['textSource'] | 'spreadsheet';
  checks: CheckResult[];
  /** Why the pipeline could not read the page, in plain words; null once read. */
  failingReason: string | null;
  /** A flat page's layout once its header was found; absent on builder pages and on pages not read. */
  layout?: PricebookFlatLayout;
};

/** Where a registry value is printed: the words it was rebuilt from, on one page of the edition. */
export type PricebookCitation = {
  /** 1-based. */
  page: number;
  /** `${page}:${index}` word ids on the edition's evidence. Empty means uncited. */
  citedWordIds: string[];
};

/** One allowed option of a builder segment, such as `BLK` for Finish. */
export type PricebookSegmentOption = PricebookCitation & {
  code: string;
  /** The option's printed description, such as "Black"; null when none is printed. */
  label: string | null;
};

/** One builder segment, such as Type or Finish, in part-number order. */
export type PricebookSegment = PricebookCitation & {
  name: string;
  /** 0-based position in the part number. */
  position: number;
  options: PricebookSegmentOption[];
};

/** One segment option a price rule applies to. */
export type PricebookOptionRef = {
  segment: string;
  code: string;
};

/**
 * One price rule of a builder. A part number's price is the one `base` rule
 * whose `when` it matches plus every `adder` whose `when` it matches; no
 * matching base, or more than one, leaves it unpriced. `when` matches when,
 * for each segment it names, the part number's option is one of the codes it
 * names for that segment, so `Finish BLK, BRZ +$150` is one adder. An empty
 * `when` matches every part number of the series.
 */
export type PricebookPriceRule = PricebookCitation & {
  kind: 'base' | 'adder';
  when: PricebookOptionRef[];
  amount: number;
  /** Set when a person corrected or added the rule in S4; a correction keeps the rule's cited words. */
  edited?: PricebookAct;
};

/**
 * One document at `pricebookEditions/{editionId}/rules/{ruleId}`: the
 * part-number builder of one series on one product page. `ruleId` is
 * `p<page>-<series>`, with `-<n>` added for the n-th (from 2) builder of the
 * same series on the page. Part numbers are expanded from these on demand and never
 * stored. `sample` is the part number and price the page prints, which the
 * rules must rebuild (`sampleRebuild`).
 */
export type PricebookRule = PricebookCitation & {
  docId: DocId;
  series: string;
  /** Printed between segments, such as `-`. */
  separator: string;
  segments: PricebookSegment[];
  priceRules: PricebookPriceRule[];
  /** ISO 4217. */
  currency: string;
  /** Null when the page prints no sample. */
  sample: (PricebookCitation & { partNumber: string; price: number }) | null;
};

/**
 * One document at `pricebookEditions/{editionId}/parts/{partId}`: one row of a
 * flat price table. `partId` is `pricebookPartId(partNumber)`, since a part
 * number may hold `/` or read as a reserved Firestore id.
 */
export type PricebookPart = PricebookCitation & {
  docId: DocId;
  partNumber: string;
  /** Other printed codes of the row (such as a confirmation code), keyed by the Parts field they map to. */
  codes: Record<string, string>;
  price: number;
  /** ISO 4217, confirmed from the header or the cover in S3. */
  currency: string;
  /** The series heading the row is printed under; null when none. */
  series: string | null;
  /** 0-based column group of the page the row is printed in, which `rowCount` counts it under. */
  group: number;
  /**
   * Set when a person corrected or added the row in S4. A correction keeps the
   * row's cited words, and `corrects` is the id of the part the page's words
   * gave, so deriving the page again leaves that one out; null for an added row.
   */
  edited?: PricebookAct & { corrects: string | null };
};

/**
 * One fix a person makes to a draft page in S4, with no re-read. `part`
 * corrects the part `replaces` names (its id) or, with null, adds a row to
 * column group `group` of a flat page. `priceRule` corrects price rule
 * `index` of builder rule `ruleId`, or adds one with null. The server keeps
 * the cited words of what it corrects, stamps `edited`, and derives the
 * page's checks again.
 */
export type PricebookPageFix =
  | {
      kind: 'part';
      replaces: string | null;
      part: Pick<PricebookPart, 'partNumber' | 'price' | 'group' | 'series'>;
    }
  | {
      kind: 'priceRule';
      ruleId: string;
      index: number | null;
      priceRule: Pick<PricebookPriceRule, 'kind' | 'when' | 'amount'>;
    };

/**
 * The publish dry run: how open schedules would come out of the catalog
 * grammar check, one count per `ScheduleCatalogShift` (`staysAsIs`,
 * `becomesFlagged`, `changed`, which add up to the open schedules), and
 * `flagged`, every one left with a failed check after. The re-check counts the
 * same four as it goes (`PricebookRecheckProgress`) and ends on them.
 */
export type PricebookRecheckDryRun = {
  staysAsIs: number;
  becomesFlagged: number;
  changed: number;
  flagged: number;
};

/** One change kind, as S5's Segmented filters name them. */
export type PricebookChangeKind = 'added' | 'removed' | 'repriced' | 'unchanged';

/** Where one side of a change is printed. Opens S4 on that edition and page. */
export type PricebookChangeSide = {
  editionId: string;
  /** 1-based. */
  page: number;
};

/**
 * What a builder change touches: a segment's allowed option, a base price or
 * an adder, each named by the options it applies to.
 */
export type PricebookRuleChangeSubject =
  | { kind: 'segmentOption'; segment: string; code: string }
  | { kind: 'basePrice'; when: PricebookOptionRef[] }
  | { kind: 'adder'; when: PricebookOptionRef[] };

/**
 * The open-schedule impact every change carries: `openSchedules`, the
 * schedules citing an affected part number, and `flaggedSchedules`, those the
 * change itself flags: an affected number of theirs that did not fail the
 * catalog grammar check fails it once the next edition is published
 * (`scheduleCatalogShift` over the affected numbers). Only an added or
 * removed segment option or part changes what parses, so a price rule, or a
 * repriced or unchanged change, flags none.
 */
export type PricebookChangeImpact = {
  openSchedules: number;
  flaggedSchedules: number;
};

/** One builder change. `partCount` is how many part numbers the rule produces on the next side, else the base side. */
export type PricebookRuleChange = PricebookChangeImpact & {
  level: 'rule';
  kind: PricebookChangeKind;
  ruleId: string;
  series: string;
  subject: PricebookRuleChangeSubject;
  /** Null for `added`; `amount` is null for a segment option. */
  base: (PricebookChangeSide & { amount: number | null }) | null;
  /** Null for `removed`. */
  next: (PricebookChangeSide & { amount: number | null }) | null;
  /** ISO 4217 of the rule's amounts, from the next side's rule, else the base side's. */
  currency: string;
  partCount: number;
};

/** One flat-table change, part by part. */
export type PricebookPartChange = PricebookChangeImpact & {
  level: 'part';
  kind: PricebookChangeKind;
  partNumber: string;
  series: string | null;
  /** Null for `added`. */
  base: (PricebookChangeSide & { price: number; currency: string }) | null;
  /** Null for `removed`. */
  next: (PricebookChangeSide & { price: number; currency: string }) | null;
};

export type PricebookChange = PricebookRuleChange | PricebookPartChange;

/**
 * The S5 comparison. `changes`: rule level for builder pages and part level
 * for flat pages, so a mixed edition carries both. `firstEdition`: there is no
 * base, so the view lists parts by series with counts.
 */
export type PricebookEditionComparison =
  | {
      kind: 'changes';
      baseEditionId: string;
      nextEditionId: string;
      baseSourceSha256: string;
      nextSourceSha256: string;
      changes: PricebookChange[];
    }
  | {
      kind: 'firstEdition';
      nextEditionId: string;
      nextSourceSha256: string;
      series: { series: string; partCount: number; pages: number[] }[];
    };

/**
 * The pricebook tasks sent as `request.data` to `/runlanguagemodel`,
 * dispatched by `handlePricebookTask`. Every task needs `pricebookRead`,
 * `aiOperator` or `aiAdmin`; a publish that is not a dry run also needs
 * `pricebookPublish` or `aiAdmin`.
 * `publishPricebookEdition`: with `dryRun`, runs the catalog grammar check of
 * every open schedule of the manufacturer against the edition and writes
 * nothing; without, refuses unless the edition is `awaitingSignoff`, then in
 * one transaction sets it `published` and the manufacturer's published
 * edition for the same price list `superseded`, appends `publishEdition` and
 * `supersede`, and runs the re-check in the same request, answering once it
 * finishes; publishing an edition already published only resumes it.
 * `recheckPricebookEdition`: runs the re-check of a published edition, or
 * resumes it, skipping schedules already checked against it.
 * `comparePricebookEditions`: S5's comparison; `baseEditionId` null for a
 * first edition.
 * `requestPricebookSignoff`: sets `signoffRequest`.
 * `savePricebookColumnMapping`: S3 stores the mapping of one header on a
 * draft edition and derives every flat page printing that header again.
 * `savePricebookPageFix`: applies one `PricebookPageFix` to a draft page and
 * derives its checks again. Both are refused while a pipeline run holds the
 * edition's job.
 * `startPdfIntakePricebook`: runs a file the caller uploaded to their own
 * `fileFolder` (`temp/<uid>/<fileFolderId>`) as a pricebook, as the caller:
 * a `.pdf` through the PDF pricebook intake, an `.xlsx` or `.csv` through the
 * spreadsheet one; any other extension is refused. `edition` names the
 * edition the file opens, since nothing reads it off the cover. The job and
 * edition ids are the file's SHA-256, so the same bytes open the earlier
 * edition. Answered with `PricebookTaskResult`.
 */
export type PricebookTaskRequest =
  | { taskType: 'publishPricebookEdition'; editionId: string; dryRun?: boolean }
  | { taskType: 'recheckPricebookEdition'; editionId: string }
  | { taskType: 'comparePricebookEditions'; baseEditionId: string | null; nextEditionId: string }
  | { taskType: 'requestPricebookSignoff'; editionId: string }
  | { taskType: 'savePricebookColumnMapping'; editionId: string; headerKey: string; mapping: FlatHeaderMapping }
  | { taskType: 'savePricebookPageFix'; editionId: string; page: number; fix: PricebookPageFix }
  | {
      taskType: 'startPdfIntakePricebook';
      fileFolder: string;
      filename: string;
      edition: Pick<PricebookEdition, 'manufacturerId' | 'priceListKey' | 'name' | 'effectiveDate'>;
    };

/** A refused or failed pricebook task. */
export type PricebookTaskFailure = { success: false; error: string };

/** `dryRun` is set exactly when the request asked for a dry run. */
export type PublishPricebookEditionResult = { success: true; dryRun: PricebookRecheckDryRun | null } | PricebookTaskFailure;

export type ComparePricebookEditionsResult = { success: true; comparison: PricebookEditionComparison } | PricebookTaskFailure;

/** The result of `recheckPricebookEdition`, `requestPricebookSignoff`, `savePricebookColumnMapping`, `savePricebookPageFix` and `startPdfIntakePricebook`. */
export type PricebookTaskResult = { success: true } | PricebookTaskFailure;

/** Any pricebook task's response, as the client receives it before narrowing by the task it sent. */
export type PricebookTaskResponse = PublishPricebookEditionResult | ComparePricebookEditionsResult | PricebookTaskResult;
